import { useState, type FormEvent } from 'react';
import {
  Bouton, Carte, Champ, GrilleFormulaire, IconeCorbeille, IconePlus,
  Message, Selecteur, Tableau, Vide, useRetours,
} from '@journal/ui';
import { useSession } from '../lib/session.tsx';
import { NIVEAUX_DISPONIBLES, basculerNiveau } from './ReglagesGeneral.tsx';
import { useDonnees, useEcriture } from '../lib/donnees.ts';
import type { Membre } from '../lib/api.ts';

/**
 * Les classes de l'école, et qui en a la charge.
 *
 * ── Pourquoi ici plutôt que dans les réglages ───────────────────────────────
 * Répartir les classes, c'est répartir des personnes : le geste appartient au
 * même écran que la gestion des comptes. Les séparer obligerait le directeur à
 * faire l'aller-retour à chaque rentrée, en tenant deux listes de tête.
 *
 * ── Une classe vide se supprime, une classe habitée non ─────────────────────
 * Journal, progressions et emploi du temps partent en cascade avec elle. Le
 * serveur refuse tant qu'il reste des élèves ; l'interface le dit d'avance
 * plutôt que de laisser découvrir le refus après le clic.
 */

export interface Classe {
  id: string;
  name: string;
  levels: string;
  teacherId: string | null;
  teacherEmail: string | null;
  schoolYearId: string | null;
  position: number;
  studentCount: number;
}

export function GestionClasses({ membres, estResponsable }: {
  membres: Membre[];
  /** Seul un responsable crée, renomme et affecte. */
  estResponsable: boolean;
}) {
  const { api } = useSession();
  const { notifier, confirmer } = useRetours();
  const { executer, erreur } = useEcriture();

  const classes = useDonnees<Classe[]>('/classrooms');
  const [nom, setNom] = useState('');
  const [niveaux, setNiveaux] = useState<string[]>([]);
  const [titulaire, setTitulaire] = useState('');
  const [enCours, setEnCours] = useState(false);

  async function creer(evenement: FormEvent) {
    evenement.preventDefault();
    setEnCours(true);
    const ok = await executer(() =>
      api.requete('/classrooms', {
        methode: 'POST',
        corps: {
          name: nom.trim(),
          levels: niveaux.join(','),
          // Chaîne vide = pas de titulaire : une classe peut exister entre deux
          // rentrées sans que personne en ait la charge.
          teacherId: titulaire || null,
        },
      }));
    setEnCours(false);
    if (ok) {
      notifier(`Classe « ${nom.trim()} » créée.`);
      setNom('');
      setNiveaux([]);
      setTitulaire('');
      await classes.recharger();
    }
  }

  async function affecter(classe: Classe, teacherId: string) {
    const ok = await executer(() =>
      api.requete(`/classrooms/${classe.id}`, {
        methode: 'PATCH',
        corps: { teacherId: teacherId || null },
      }));
    if (ok) {
      notifier('Titulaire mis à jour.');
      await classes.recharger();
    }
  }

  async function supprimer(classe: Classe) {
    const accord = await confirmer({
      titre: `Supprimer « ${classe.name} » ?`,
      message:
        'Le cahier journal, les progressions, la programmation et l’emploi du '
        + 'temps de cette classe seront supprimés avec elle.',
      libelleConfirmation: 'Supprimer',
      destructeur: true,
    });
    if (!accord) return;

    const ok = await executer(() =>
      api.requete(`/classrooms/${classe.id}`, { methode: 'DELETE' }));
    if (ok) {
      notifier('Classe supprimée.');
      await classes.recharger();
    }
  }

  const liste = classes.donnees ?? [];

  return (
    <Carte
      titre="Classes"
      description={
        estResponsable
          ? 'Chaque classe a ses élèves, son cahier journal et ses progressions.'
          : 'Les classes dont vous avez la charge.'
      }
    >
      {erreur && <Message ton="erreur">{erreur}</Message>}
      {classes.erreur && <Message ton="erreur">{classes.erreur}</Message>}

      {liste.length === 0 ? (
        <Vide titre="Aucune classe">
          {estResponsable
            ? 'Créez une classe et confiez-la à un enseignant.'
            : 'Aucune classe ne vous est confiée pour l’instant.'}
        </Vide>
      ) : (
        <Tableau
            redimensionnable="classes"
          legende="Classes de l’école, avec leur titulaire et leur effectif"
          lignes={liste}
          cleLigne={(c) => c.id}
          zebre={liste.length >= 6}
          colonnes={[
            { cle: 'nom', entete: 'Classe', cellule: (c) => c.name },
            {
              cle: 'niveaux',
              entete: 'Niveaux',
              largeur: '9rem',
              cellule: (c) => c.levels || '—',
            },
            {
              cle: 'titulaire',
              entete: 'Titulaire',
              largeur: '18rem',
              cellule: (c) =>
                estResponsable ? (
                  <Selecteur
                    libelle={`Titulaire de ${c.name}`}
                    libelleMasque
                    value={c.teacherId ?? ''}
                    onChange={(e) => void affecter(c, e.target.value)}
                    options={[
                      { valeur: '', texte: 'Sans titulaire' },
                      ...membres.map((m) => ({ valeur: m.userId, texte: m.email })),
                    ]}
                  />
                ) : (c.teacherEmail ?? '—'),
            },
            {
              cle: 'eleves',
              entete: 'Élèves',
              numerique: true,
              largeur: '7rem',
              cellule: (c) => c.studentCount,
            },
            ...(estResponsable ? [{
              cle: 'actions',
              entete: 'Actions',
              largeur: '8rem',
              cellule: (c: Classe) => (
                <Bouton
                  variante="ghost"
                  taille="sm"
                  icone={<IconeCorbeille taille={14} />}
                  onClick={() => void supprimer(c)}
                  aria-label={`Supprimer la classe ${c.name}`}
                  // Le serveur refuse aussi : le bouton désactivé évite
                  // seulement d'aller au bout d'un geste condamné.
                  disabled={c.studentCount > 0}
                  title={
                    c.studentCount > 0
                      ? 'Retirez d’abord les élèves de cette classe'
                      : undefined
                  }
                >
                  Supprimer
                </Bouton>
              ),
            }] : []),
          ]}
        />
      )}

      {estResponsable && (
        <form onSubmit={creer} className="mt-5 flex flex-col gap-3">
          {/*
            * Un TITRE, comme « Ajouter un collègue » juste à côté.
            *
            * Le formulaire suivait le tableau sans rien annoncer : deux cartes
            * voisines, l'une titrée et l'autre non, et l'on cherchait où créer
            * une classe alors que le champ était sous les yeux.
            */}
          <h3>Ajouter une classe</h3>
          <GrilleFormulaire>
            <Champ
              libelle="Nom de la classe"
              value={nom}
              onChange={(e) => setNom(e.target.value)}
              aide="« CE2 A », « Classe de Mme Martin »…"
              required
            />
            {/*
              * Des BOUTONS, pas un champ texte.
              *
              * Le niveau est une valeur fermée : « CE2 » mal orthographié, ou
              * « ce2 » en minuscules, ne correspondrait à aucun domaine du
              * programme, et la classe se retrouverait avec un référentiel
              * vide sans que rien ne l'explique. Un double niveau — le cas
              * ordinaire en primaire — se compose en cochant deux boutons.
              */}
            <div>
              <span className="mb-1.5 block text-[13px] font-medium">Niveaux</span>
              <div
                role="group"
                aria-label="Niveaux de la classe"
                className="grid grid-cols-5 gap-1.5 sm:grid-cols-9"
              >
                {NIVEAUX_DISPONIBLES.map((n) => {
                  const actif = niveaux.includes(n);
                  return (
                    <button
                      key={n}
                      type="button"
                      aria-pressed={actif}
                      onClick={() => setNiveaux(basculerNiveau(niveaux, n))}
                      className={[
                        'rounded-lg border px-2 py-1.5 text-[12px] font-medium',
                        'transition-colors duration-150 motion-reduce:transition-none',
                        'focus-visible:outline-2 focus-visible:outline-offset-1',
                        'focus-visible:outline-[var(--color-focus)]',
                        actif
                          ? 'border-[var(--color-primary)] bg-[var(--color-primary-soft)] text-[var(--color-primary-soft-fg)]'
                          : 'border-[var(--color-border)] hover:bg-[var(--color-surface-hover)]',
                      ].join(' ')}
                    >
                      {n}
                    </button>
                  );
                })}
              </div>
            </div>
            <Selecteur
              libelle="Titulaire"
              value={titulaire}
              onChange={(e) => setTitulaire(e.target.value)}
              options={[
                { valeur: '', texte: 'Sans titulaire pour l’instant' },
                ...membres.map((m) => ({ valeur: m.userId, texte: m.email })),
              ]}
            />
          </GrilleFormulaire>
          <div>
            <Bouton
              type="submit"
              variante="primary"
              icone={<IconePlus taille={16} />}
              chargement={enCours}
              disabled={!nom.trim()}
            >
              Créer la classe
            </Bouton>
          </div>
        </form>
      )}
    </Carte>
  );
}
